import React, { useState, useEffect, useRef } from 'react';
import { SiteContentService, SingAlongBranding } from '../services/SiteContentService';
import { Save, Plus, Trash2, Loader2, Palette, Image, Link } from 'lucide-react';

export const AdminSingAlongBranding: React.FC = () => {
  const [branding, setBranding] = useState<SingAlongBranding>({
    logo_url: null,
    sponsor_logos: [],
    sponsor_links: [],
    primary_color: '#f97316',
    accent_color: '#f59e0b',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const sponsorInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await SiteContentService.getSingAlongBranding();
        setBranding(data);
      } catch (err) {
        console.error('Failed to load branding:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSuccessMsg('');
    try {
      await SiteContentService.saveSingAlongBranding(branding);
      setSuccessMsg('Branding saved successfully');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      console.error('Failed to save branding:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSponsorUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await SiteContentService.uploadSingAlongAsset(file, 'sponsor');
      setBranding((b) => ({
        ...b,
        sponsor_logos: [...b.sponsor_logos, url],
        sponsor_links: [...b.sponsor_links, ''],
      }));
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploading(false);
      if (sponsorInputRef.current) sponsorInputRef.current.value = '';
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const url = await SiteContentService.uploadSingAlongAsset(file, 'logo');
      setBranding((b) => ({ ...b, logo_url: url }));
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const removeSponsor = (index: number) => {
    setBranding((b) => ({
      ...b,
      sponsor_logos: b.sponsor_logos.filter((_, i) => i !== index),
      sponsor_links: b.sponsor_links.filter((_, i) => i !== index),
    }));
  };

  const setSponsorLink = (index: number, value: string) => {
    setBranding((b) => ({
      ...b,
      sponsor_links: b.sponsor_links.map((l, i) => (i === index ? value : l)),
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-orange-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white">Email Branding</h2>
        <p className="text-sm text-slate-400 mt-1">
          Configure the default branding shown in verification emails for new Sing-Along signups.
          These sponsors and colors appear when no league-specific branding is set.
        </p>
      </div>

      <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-6 space-y-6 max-w-3xl">
        {/* Colors */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Palette className="w-4 h-4 text-slate-400" />
            <h3 className="text-sm font-semibold text-slate-200">Email Colors</h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Primary Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={branding.primary_color}
                  onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                  className="w-10 h-10 rounded border border-slate-600 cursor-pointer bg-transparent"
                />
                <input
                  type="text"
                  value={branding.primary_color}
                  onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                  className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 text-white rounded-lg text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Accent Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={branding.accent_color}
                  onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })}
                  className="w-10 h-10 rounded border border-slate-600 cursor-pointer bg-transparent"
                />
                <input
                  type="text"
                  value={branding.accent_color}
                  onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })}
                  className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 text-white rounded-lg text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Logo */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Image className="w-4 h-4 text-slate-400" />
            <h3 className="text-sm font-semibold text-slate-200">Logo</h3>
          </div>
          <p className="text-xs text-slate-400 mb-2">Shown at the top of the verification email. PNG or SVG with transparent background works best.</p>
          {branding.logo_url && (
            <div className="flex items-center gap-3 mb-3 bg-slate-900/50 border border-slate-700 rounded-lg p-3">
              <img src={branding.logo_url} alt="Logo" className="h-12 object-contain bg-white/5 rounded px-2" />
              <button
                type="button"
                onClick={() => setBranding({ ...branding, logo_url: null })}
                className="p-2 text-red-400 hover:text-red-300"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
          <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
          <button
            type="button"
            onClick={() => logoInputRef.current?.click()}
            disabled={uploadingLogo}
            className="inline-flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm disabled:opacity-50"
          >
            {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {branding.logo_url ? 'Replace logo' : 'Upload logo'}
          </button>
        </div>

        {/* Sponsors */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Link className="w-4 h-4 text-slate-400" />
            <h3 className="text-sm font-semibold text-slate-200">Sponsor Logos</h3>
          </div>
          <p className="text-xs text-slate-400 mb-3">These logos appear in verification emails under a "Presented by" section.</p>
          <div className="space-y-2 mb-3">
            {branding.sponsor_logos.length === 0 && (
              <p className="text-xs text-slate-500 italic">No sponsors yet. Upload a logo below.</p>
            )}
            {branding.sponsor_logos.map((logo, i) => (
              <div key={i} className="flex items-center gap-3 bg-slate-900/50 border border-slate-700 rounded-lg p-2">
                <img src={logo} alt="Sponsor" className="h-10 object-contain bg-white/5 rounded px-2" />
                <input
                  type="url"
                  value={branding.sponsor_links[i] || ''}
                  onChange={(e) => setSponsorLink(i, e.target.value)}
                  placeholder="https://sponsor-website.com (optional)"
                  className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 text-white rounded-lg text-sm placeholder-slate-500"
                />
                <button
                  type="button"
                  onClick={() => removeSponsor(i)}
                  className="p-2 text-red-400 hover:text-red-300"
                  title="Remove sponsor"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <input ref={sponsorInputRef} type="file" accept="image/*" className="hidden" onChange={handleSponsorUpload} />
          <button
            type="button"
            onClick={() => sponsorInputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm disabled:opacity-50"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add sponsor logo
          </button>
        </div>

        {/* Save */}
        <div className="flex items-center gap-3 pt-2 border-t border-slate-700">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg text-sm disabled:opacity-50 transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving...' : 'Save Branding'}
          </button>
          {successMsg && (
            <span className="text-sm text-green-400 font-medium">{successMsg}</span>
          )}
        </div>
      </div>
    </div>
  );
};
